import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Loading from '../components/atoms/Loading';
import { getFormFields } from '../redux/complaint/actions';
import { useDispatch } from 'react-redux';
import api from '../utils/api';
import './querymanagement.css';

const DEFAULT_COMPLAINT_TYPE_ID = 37;

const FIELD_TYPE_MAP = {
	CUSTOM: 'Custom',
	TEXT: 'Text',
	TEXTAREA: 'Text Area',
	SELECT: 'Select',
	DROPDOWN: 'Dropdown',
	NUMBER: 'Number',
	DATE: 'Date',
	DATETIME: 'Date Time',
	EMAIL: 'Email',
	MULTISELECT: 'MultiSelect',
	CHECKBOX: 'Checkbox',
	RADIO: 'Radio',
	FILE: 'File',
};

function optionLabel(value) {
	if (typeof value === 'string') return value;
	return value?.label ?? '';
}

function normalizeFormFields(list = []) {
	const sorted = [...list].sort(
		(a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
	);

	return sorted.map((item) => ({
		id: item.id,
		label: item.fieldName ?? item.fieldLabel ?? item.label ?? '',
		fieldKey: item.fieldKey ?? String(item.id),
		fieldType: FIELD_TYPE_MAP[item.fieldType] ?? item.fieldType ?? 'Custom',
		required: Boolean(item.required),
		options: (item?.options?.values ?? []).map(optionLabel).filter(Boolean),
	}));
}

function fieldName(field) {
	return String(field.fieldKey || field.id || field.label);
}

function fieldType(field) {
	const type = String(field.fieldType || '').toLowerCase();
	if (type === 'textarea' || type === 'text area') return 'textarea';
	if (type === 'date') return 'date';
	if (type === 'date time' || type === 'datetime' || type === 'datetime-local') return 'datetime-local';
	if (type === 'email') return 'email';
	if (type === 'number') return 'number';
	if (type === 'radio') return 'radio';
	if (type === 'checkbox') return 'checkbox';
	if (type === 'multiselect' || type === 'dropdown' || type === 'select' || type === 'custom') {
		return field.options.length ? 'select' : 'text';
	}
	return 'text';
}

function questionId(field) {
	return `new-query-${field.id ?? fieldName(field)}`;
}

function initialValue(field) {
	return fieldType(field) === 'checkbox' ? [] : '';
}

function NewQueryControl({ field, value, onChange }) {
	const type = fieldType(field);
	const id = questionId(field);

	if (type === 'radio') {
		return (
			<div className="review-options review-options--inline">
				{field.options.map((option) => (
					<label className="review-option" htmlFor={`${id}-${option}`} key={option}>
						<input
							id={`${id}-${option}`}
							name={fieldName(field)}
							type="radio"
							value={option}
							checked={value === option}
							onChange={(event) => onChange(event.target.value)}
							required={field.required}
						/>
						<span>{option}</span>
					</label>
				))}
			</div>
		);
	}

	if (type === 'checkbox') {
		const selected = Array.isArray(value) ? value : [];
		return (
			<div className="review-options review-options--inline">
				{field.options.map((option) => (
					<label className="review-option" htmlFor={`${id}-${option}`} key={option}>
						<input
							id={`${id}-${option}`}
							name={`${fieldName(field)}[]`}
							type="checkbox"
							value={option}
							checked={selected.includes(option)}
							onChange={(event) => onChange(
								event.target.checked
									? [...selected, option]
									: selected.filter((item) => item !== option),
							)}
						/>
						<span>{option}</span>
					</label>
				))}
			</div>
		);
	}

	if (type === 'textarea') {
		return <textarea id={id} name={fieldName(field)} className="review-control review-control--textarea" value={value} onChange={(event) => onChange(event.target.value)} required={field.required} />;
	}

	if (type === 'select') {
		return (
			<select id={id} name={fieldName(field)} className="review-control" value={value} onChange={(event) => onChange(event.target.value)} required={field.required}>
				<option value="">Select {field.label.toLowerCase()}</option>
				{field.options.map((option) => <option key={option} value={option}>{option}</option>)}
			</select>
		);
	}

	return <input id={id} name={fieldName(field)} className="review-control" type={type} value={value} onChange={(event) => onChange(event.target.value)} required={field.required} />;
}

export default function NewQuery() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { complaintTypeId } = useParams();
    // const complaintTypeId = 37; // Hardcoded complaintTypeId for testing purposes
	const complaintId = Number(complaintTypeId) || DEFAULT_COMPLAINT_TYPE_ID;
	const [fields, setFields] = useState([]);
	const [values, setValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	useEffect(() => {
		let cancelled = false;
		// setIsLoading(true);
		dispatch(getFormFields(complaintId))
			.unwrap()
			.then((response) => {
				if (cancelled) return;
				const nextFields = normalizeFormFields(Array.isArray(response?.data) ? response.data : response);
				setFields(nextFields);
				setValues(Object.fromEntries(nextFields.map((field) => [fieldName(field), initialValue(field)])));
			})
			.catch((requestError) => {
				if (!cancelled) setError(requestError?.message || 'Failed to load form fields.');
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => { cancelled = true; };
	}, [complaintId, dispatch]);

	const hasFields = useMemo(() => fields.length > 0, [fields]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');
		setSuccess('');
		setIsSaving(true);
        console.log('Submitting query with values:', values);
		try {
			await api.post('/api/v1/complaints', {
				complaintTypeId: complaintId,
                title: values.title || 'Untitled Query',
				fieldValues: values,
			});
			setSuccess('Query submitted successfully.');
            navigate('/querymanagement');
		} catch (requestError) {
			setError(requestError?.response?.data?.message || requestError?.message || 'Failed to submit query.');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<main className="ca-query-management-page">
			<section className="ca-query-details" aria-labelledby="new-query-title">
				<div className="ca-query-details__heading">
					<h1 id="new-query-title" className="ca-query-details__title">New Query</h1>
					<Button variant="outline" size="sm" onClick={() => navigate('/querymanagement')}>Back</Button>
				</div>
				{isLoading ? <Loading text="Loading form fields" /> : null}
				{!isLoading && error && !hasFields ? <p className="ca-body">{error}</p> : null}
				{!isLoading && hasFields ? (
					<form className="review-form-card" onSubmit={handleSubmit}>
						<div className="review-form-grid">
							{fields.map((field) => (
								<div className={`review-question${['textarea', 'radio', 'checkbox'].includes(fieldType(field)) ? ' review-question--wide' : ''}`} key={field.id ?? fieldName(field)}>
									<label className="review-label" htmlFor={questionId(field)}>
										{field.label || 'Untitled field'}
										{field.required ? <span className="review-required">*</span> : null}
									</label>
									<NewQueryControl
										field={field}
										value={values[fieldName(field)] ?? initialValue(field)}
										onChange={(value) => setValues((current) => ({ ...current, [fieldName(field)]: value }))}
									/>
								</div>
							))}
						</div>
						<div className="ca-query-table__actions">
							<Button type="submit" disabled={isSaving}>{isSaving ? 'Submitting...' : 'Submit Query'}</Button>
						</div>
						{success ? <p className="ca-body">{success}</p> : null}
						{error ? <p className="ca-form-error">{error}</p> : null}
					</form>
				) : null}
			</section>
		</main>
	);
}
